import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import type { ElemetalInfosData } from '~~/src/api/elementals'
import { unsaveElemental } from '~~/src/api/elementals'
import type { Elemental } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'

function update(data: Elemental | undefined) {
  if (!data)
    return data
  data.isSaved = false
  data.nSaves = data.nSaves ?? 0
  return {
    ...data,
    nSaves: data.nSaves - 1,
  }
}
function updateInfo(data: ElemetalInfosData | undefined) {
  if (!data)
    return data
  return {
    ...data,
    isSaved: false,
  }
}

function revertUpdate(data: Elemental | undefined) {
  if (!data)
    return data
  data.isSaved = true
  data.nSaves = data.nSaves ?? 0
  return {
    ...data,
    nSaves: data.nSaves + 1,
  }
}
function revertInfo(data: ElemetalInfosData | undefined) {
  if (!data)
    return data
  return {
    ...data,
    isSaved: true,
  }
}

export const useRemoveSaveElemental = () => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, { elementalType: ElementalTypeEnum; id: string; listId?: string; listType?: ElementalTypeEnum; slug?: string }>({
    mutationKey: ['unsave-elemental'],
    mutationFn: async ({ id }) => {
      return await unsaveElemental(id)
    },
    onMutate: async ({ elementalType, id, listType, slug }) => {
      queryClient.setQueriesData({
        queryKey: ['elemental', elementalType, id],
      }, update)
      if (slug) {
        queryClient.setQueriesData({
          queryKey: ['elemental', elementalType, slug],
        }, update)
      }
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, id],
      }, updateInfo)
      const type = listType ?? elementalType

      const queryData = queryClient.getQueriesData({ queryKey: ['profile-list-elementals', type], exact: false })
      if (!queryData || queryData.length < 1)
        return
      const [_, values] = queryClient.getQueriesData({ queryKey: ['profile-list-elementals', type], exact: false })?.[0]
      if (values) {
        queryClient.setQueriesData<List[]>({
          queryKey: ['profile-list-elementals', type],
          exact: false,
        }, (old) => {
          if (!old)
            return []
          if (elementalType !== ElementalTypeEnum.List) {
            return old.map((list) => {
              if ((list.id as unknown as number) === 0) {
                return {
                  ...list,
                  prompts: list.prompts ? list.prompts.filter(e => e.id !== id) : list.prompts,
                }
              }
              return list
            })
          }
          return old.filter((list) => {
            return list.id !== id
          })
        })
      }
    },
    onError: (_, { elementalType, listId, id, slug }) => {
      queryClient.setQueriesData({
        queryKey: ['elemental', elementalType, id],
      }, revertUpdate)
      if (slug) {
        queryClient.setQueriesData({
          queryKey: ['elemental', elementalType, slug],
        }, revertUpdate)
      }
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, id],
      }, revertInfo)
      const type = listId ?? elementalType
      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', type],
        exact: false,
        type: 'all',
      })
    },
    onSuccess: (_, { elementalType, listId }) => {
      const type = listId ?? elementalType
      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', type],
        exact: false,
        type: 'all',
      })
    },
  })
}
